/**
 * The audit trail: who did what, to what, and when.
 *
 * Every other record in this product answers "what is true now". This one
 * answers "how did it get that way", which is a different question and the only
 * one that survives somebody changing their mind about what they did. The
 * product spends real money on third-party lookups and hands personal data out
 * as spreadsheets, and both of those need a name against them.
 *
 * Three rules hold the design together:
 *
 *   1. The vocabulary is CLOSED. An audit log whose action names are free text
 *      cannot be filtered, and a filter that silently misses events is worse
 *      than no filter at all — it reads as "nobody did that".
 *   2. An event is written for the ATTEMPT, not for the success. A refused
 *      export and a failed one are exactly the events an investigation is
 *      looking for, and a log that only records what worked cannot show either.
 *   3. Nothing here is ever rewritten. Rows are appended and read; there is no
 *      update path in the repository at all.
 */
/** Every action worth a name. Adding one is a deliberate act; see the notes above. */
export const AUDIT_ACTIONS = [
    // -- a batch of companies, from upload to finish --
    'job.create',
    'job.configure',
    'job.start',
    'job.pause',
    'job.resume',
    'job.cancel',
    'job.retry',
    'job.delete',
    'company.rerun',
    // -- data leaving the product --
    'export.request',
    'export.download',
    // -- money --
    'contact.enrich',
    // -- decisions about people --
    'review.accept',
    'review.reject',
    'review.auto',
    // -- edits --
    'contact.update',
    'outreach.import',
    // -- configuration --
    'settings.update',
    'settings.provider',
    // -- the repair ladder acting on a company nobody was found at --
    'repair.auto',
    'repair.queue',
    'repair.accept',
    'repair.reject',
    // -- Zoho CRM, in both directions --
    'crm.connect',
    'crm.disconnect',
    'crm.push',
    'crm.pull',
    'crm.import',
    'crm.link',
    'crm.merge',
    // -- a person leaving the product for good, or being kept out of it --
    'contact.erase',
    'suppression.add',
    'suppression.remove',
    // -- who may use Quarry, and with what rights --
    'user.promote',
    'user.demote',
    'user.disable',
    'user.enable',
    'user.signout',
    'user.invite',
    'user.uninvite',
    'user.inviteEmail',
];
/**
 * How an attempt ended.
 *
 * `denied` is separate from `error` on purpose: one is the product refusing a
 * person and the other is the product failing them, and only the first is worth
 * anybody's attention at three in the morning.
 */
export const AUDIT_OUTCOMES = ['ok', 'denied', 'error'];
/** What the action acted on, so an event can be traced back to a record. */
export const AUDIT_TARGETS = [
    'job',
    'company',
    'contact',
    'export',
    'review',
    'settings',
    'crm',
    'user',
];
/**
 * The label, grouping and weight of every action, in one place.
 *
 * The screen renders from this rather than from a switch of its own, so an
 * action added to the vocabulary above cannot appear in the log as a bare
 * `contact.enrich` that nobody outside the codebase can read.
 */
export const AUDIT_ACTION_SPEC = {
    'job.create': { label: 'Uploaded a list', group: 'Jobs', weight: 'routine' },
    'job.configure': { label: 'Configured a job', group: 'Jobs', weight: 'routine' },
    'job.start': { label: 'Started a job', group: 'Jobs', weight: 'costly' },
    'job.pause': { label: 'Paused a job', group: 'Jobs', weight: 'notable' },
    'job.resume': { label: 'Resumed a job', group: 'Jobs', weight: 'costly' },
    'job.cancel': { label: 'Cancelled a job', group: 'Jobs', weight: 'notable' },
    'job.retry': { label: 'Retried failed rows', group: 'Jobs', weight: 'costly' },
    'job.delete': { label: 'Deleted a job', group: 'Jobs', weight: 'notable' },
    'company.rerun': { label: 'Re-ran one company', group: 'Jobs', weight: 'costly' },
    'export.request': { label: 'Requested an export', group: 'Data out', weight: 'costly' },
    'export.download': { label: 'Downloaded an export', group: 'Data out', weight: 'costly' },
    'contact.enrich': { label: 'Bought contact details', group: 'Spending', weight: 'costly' },
    'review.accept': { label: 'Accepted a contact', group: 'Review', weight: 'routine' },
    'review.reject': { label: 'Rejected a contact', group: 'Review', weight: 'routine' },
    'review.auto': { label: 'Ran the automated reviewer', group: 'Review', weight: 'notable' },
    'contact.update': { label: 'Edited a contact', group: 'Contacts', weight: 'routine' },
    'outreach.import': { label: 'Imported outreach outcomes', group: 'Contacts', weight: 'notable' },
    'settings.update': { label: 'Changed settings', group: 'Settings', weight: 'notable' },
    'settings.provider': { label: 'Switched data source', group: 'Settings', weight: 'notable' },
    'repair.auto': { label: 'Repaired a company automatically', group: 'Repair', weight: 'costly' },
    'repair.queue': { label: 'Queued a company for repair review', group: 'Repair', weight: 'routine' },
    'repair.accept': { label: 'Accepted a repair', group: 'Repair', weight: 'costly' },
    'repair.reject': { label: 'Rejected a repair', group: 'Repair', weight: 'notable' },
    'crm.connect': { label: 'Connected Zoho CRM', group: 'CRM', weight: 'notable' },
    'crm.disconnect': { label: 'Disconnected Zoho CRM', group: 'CRM', weight: 'notable' },
    'crm.push': { label: 'Sent contacts to Zoho CRM', group: 'CRM', weight: 'costly' },
    'crm.pull': { label: 'Applied changes from Zoho CRM', group: 'CRM', weight: 'routine' },
    'crm.import': { label: 'Imported Zoho CRM records', group: 'CRM', weight: 'notable' },
    'crm.link': { label: 'Linked a contact to a Zoho CRM record', group: 'CRM', weight: 'routine' },
    'crm.merge': { label: 'Merged duplicate Zoho CRM records', group: 'CRM', weight: 'costly' },
    'contact.erase': { label: 'Erased a contact', group: 'Contacts', weight: 'costly' },
    'suppression.add': { label: 'Suppressed a contact', group: 'Contacts', weight: 'notable' },
    'suppression.remove': { label: 'Lifted a suppression', group: 'Contacts', weight: 'notable' },
    'user.promote': { label: 'Made a user an administrator', group: 'Users', weight: 'notable' },
    'user.demote': { label: 'Removed administrator rights', group: 'Users', weight: 'notable' },
    'user.disable': { label: 'Disabled a user', group: 'Users', weight: 'notable' },
    'user.enable': { label: 'Re-enabled a user', group: 'Users', weight: 'notable' },
    'user.signout': { label: 'Signed a user out everywhere', group: 'Users', weight: 'notable' },
    'user.invite': { label: 'Invited people to Quarry', group: 'Users', weight: 'notable' },
    'user.uninvite': { label: 'Cancelled an invitation', group: 'Users', weight: 'notable' },
    'user.inviteEmail': { label: 'Emailed an invitation again', group: 'Users', weight: 'routine' },
};
/** The action groups, in the order the filter offers them. */
export const AUDIT_GROUPS = [
    'Jobs',
    'Data out',
    'Spending',
    'Review',
    'Repair',
    'Contacts',
    'CRM',
    'Settings',
    'Users',
];
/** The month partition an instant belongs to. */
export function auditMonth(at) {
    const iso = typeof at === 'string' ? at : at.toISOString();
    return iso.slice(0, 7);
}
/**
 * The month partitions to read, newest first, for a range.
 *
 * Bounded by {@link AUDIT_MAX_MONTHS} so an open-ended range on an empty log
 * cannot become an unbounded walk backwards through time. Both ends are
 * inclusive, and a `from` after `to` yields nothing rather than looping.
 */
export const AUDIT_MAX_MONTHS = 36;
export function auditMonthsBetween(from, to, max = AUDIT_MAX_MONTHS) {
    const months = [];
    // Walking a Date by month rather than by string arithmetic: December to
    // January is the case a naive decrement gets wrong, and it gets it wrong once
    // a year in a way nobody notices until the log looks empty in January.
    const cursor = new Date(`${to.slice(0, 7)}-01T00:00:00.000Z`);
    const floor = `${from.slice(0, 7)}`;
    while (months.length < max) {
        const month = cursor.toISOString().slice(0, 7);
        if (month < floor)
            break;
        months.push(month);
        cursor.setUTCMonth(cursor.getUTCMonth() - 1);
    }
    return months;
}
/**
 * The sort-key bounds for a date range.
 *
 * `eventId` begins with the ISO instant, so a range over it IS a range over
 * time. The upper bound appends a character above every one an ISO timestamp can
 * contain, which is what makes `to` mean the whole of that day rather than
 * midnight at the start of it — the off-by-one that quietly hides everything
 * somebody did today.
 */
export function auditIdRange(from, to) {
    return { lo: from ? from.slice(0, 10) : '0000', hi: `${to ? to.slice(0, 10) : '9999'}￿` };
}
//# sourceMappingURL=audit.js.map